import { useState, useEffect } from "react";
import axios from "axios";
import { useOutletContext } from 'react-router-dom';
import { toast } from "react-toastify";
import { useAuth } from "../../../context/auth-context";
import { rootUrl, getAllConstituencies, formatToZMW} from "../../../helpers";
import { zambiaProvinces } from "../../../data/locations";
import { GrProjects } from "react-icons/gr";
import { FaEdit, FaFolderOpen, FaPlus, FaTimes, FaTrash, FaFileExcel } from "react-icons/fa";
import Select from "react-select";
import FormGenerator from "../../../components/formGenerator";
import * as XLSX from 'xlsx';

const ProjectPage = () => {
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [editId, setEditId] = useState(null);
  const [addButton, setAddButton] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

    const {allData, setAllData} = useOutletContext();
    const auth = useAuth();

    const  constituencies  = getAllConstituencies(zambiaProvinces);

     useEffect(() => {
          if (allData?.budget) {
            setBudgets(allData.budget);
          }
        }, [allData]); 

  useEffect(() => {
    if (allData.head) {
      setFields([
        {
          type: "select",
          name: "b_id",
          label: "Budget",
          required: true,
          options: allData.budget.map((b) => ({
            value: b.id,
            label: `${b.coa} - ${allData.program.find(a=>a.programCode == b.program)?.name || ''} -
            ${allData.subProgram.find(a=>a.subProgramCode == b.subprogram
              && a.programCode == b.program
              )?.name  || ''} 
            - ${b.toy}`,
          })),
        },
        {
          type: "select",
          name: "constituency",
          label: "Constituency",
          required: true,
          options: constituencies.map((res) => ({
            value: res,
            label: res,
          })),
        },
        {
            type: 'select',
            name: 'head',
            label: 'Head',
            required: true,
            dependentFields: ['sub'],
            options: allData.head.map(res => ({
              value: res.id,
              label: res.name,
            })),
          },
          {
            type: 'select',
            name: 'sub',
            label: 'Sub',
            required: true,
            options: allData.sub.map(res => ({
              value: res.id,
              label: res.name,
              dependsOn: 'head',
              dependentValue: res.pid,
            })),
          },
        {
          type: "text",
          name: "name",
          label: "Name",
          required: true,
        },
        {
          type: "text",
          name: "amount",
          label: "Amount",
          required: true,
        },
        {
          type: "text",
          name: "year",
          label: "Project Year",
          required: true,
        },
        {
          type: "textarea",
          name: "description",
          label: "Description",
        },
      ]);
    }
    fetchProjects();
  }, [allData]);

  const fetchProjects = async () => {
    try {
      const resp = await axios.get(rootUrl(`/getProjs/${auth.user.id}`));
      setProjects(resp.data.data);
    } catch (e) {
      toast.error("Error fetching projects");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddProject = async (payload) => {
    
    setAddButton(true);
    const data = { ...payload, uid: auth.user.id };
    try {
      await axios.post(rootUrl("/addProj"), data);
      toast.success("Project added successfully");
      fetchProjects();
      toggleModal()
      setFormData({});
      setAddButton(false);
    } catch (e) {
      toast.error("Error adding project");
    }
  };

  const handleEditProject = async (payload) => {
    
    const data = { ...payload, uid: auth.user.id };

    try {
      await axios.post(rootUrl(`/updateProj`), data);
      toast.success("Project updated successfully");
      fetchProjects();
      setFormData({});
      toggleModal()
      setEditId(null);
    } catch (e) {
      toast.error("Error updating project");
    }
  };

  const handleEdit = (project) => {
    // Map head name to ID
    const headObj = allData.head.find(h => h.name === project.head);
    // Map sub name to ID
    const subObj = allData.sub.find(s => s.name === project.sub);

    // Create a new object with IDs for head and sub
    const projectWithIds = {
      ...project,
      head: headObj ? headObj.id : '',
      sub: subObj ? subObj.id : '',
    };

    setFormData(projectWithIds);
    setSelectedProject(projectWithIds);
    toggleModal();
    setEditId(project.id);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(rootUrl(`/deleteProj/${deleteId}`));
      toast.success("Project deleted successfully");
      fetchProjects();
    } catch (e) {
      toast.error("Error deleting project");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
    setAddButton(false)
    }

    const [searchTerm, setSearchTerm] = useState("");

    const filteredProjects = projects.filter((proj) =>
  Object.values(proj).some((value) =>
    value !== null && value !== undefined && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
  )
);
  

  // Export to Excel function
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      filteredProjects.map((proj) => ({
        Budget: budgets?.find(a=>a.id == proj.b_id)?.coa + ' ' + budgets?.find(a=>a.id == proj.b_id)?.toy,
        Name: proj.name,
        Head: proj.head,
        SubHead: proj.sub,
        Description: proj.description,
        Amount: proj.amount,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Projects');
    XLSX.writeFile(wb, 'projects.xlsx');
  };

  return (
    <div className="w-full flex flex-col bg-white min-h-[400px] rounded-md mt-10 p-5">
      

       <div className="flex w-full items-center justify-between">
              <h1 className="font-bold text-2xl flex items-center">
                                <GrProjects className="mr-2" /> Projects
                              </h1>
              <div className="flex items-center">
                
                {/* Add Supplier Button */}
                <button
                  onClick={exportToExcel}
                  className="flex items-center bg-blue-950 text-white items-center border border-gray-300 h-[40px] px-3 ml-2 rounded-lg text-[12px]"
                >
                  <FaFileExcel className="mr-2" /> Export to Excel
                </button>
                <button
                  onClick={toggleModal}
                  className="flex items-center btn bg-blue-950 text-white items-center border border-gray-300 h-[40px] px-3 ml-2 rounded-lg text-[12px]"
                >
                  <FaPlus/>
                  Add Project 
                </button>
              </div>
            </div>
      
            <hr className="my-3"/>
      

      <h2 className="text-xl font-semibold mt-6">Projects List</h2>


      <input
        type="text"
        placeholder="Search projects..."
        className="w-1/4 p-2 border rounded-md mb-3"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <table className="w-full border-collapse border">
          <thead className="bg-blue-950 text-white">
          <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left">#</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Budget</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Name</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Head</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Sub head</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Description</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Amount</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
                  </tr>
          </thead>
          <tbody>
            {filteredProjects.length > 0 ? (
              filteredProjects.map((proj, index) => (
                <tr key={proj.id} className="hover:bg-gray-50">
                   <td className="border border-gray-300 px-4 py-2">{index + 1}</td>
                        <td className="border border-gray-300 px-4 py-2">{budgets?.find(a=>a.id == proj.b_id)?.coa} {budgets?.find(a=>a.id == proj.b_id)?.toy}</td>
                        <td className="border border-gray-300 px-4 py-2">{proj.name}</td>
                        <td className="border border-gray-300 px-4 py-2">{proj.head}</td>
                        <td className="border border-gray-300 px-4 py-2">{proj.sub}</td>
                        <td className="border border-gray-300 px-4 py-2">{proj.description}</td>
                        <td className="border border-gray-300 px-4 py-2 font-semibold">
                          {formatToZMW(proj.amount)}
                        </td>
                  <td className="border p-2 text-center">
                    <button
                      onClick={() => handleEdit(proj)}
                      className="text-blue-600 hover:underline mr-3"
                    >
                      <FaEdit/>
                    </button>
                    {/* <button
                      onClick={() => handleDeleteClick(proj.id)}
                      className="text-red-600 hover:underline"
                    >
                      <FaTrash/>
                    </button> */}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center p-3 text-gray-500">
                  <div className="flex flex-col justify-center items-center">
                        <FaFolderOpen className="text-gray-500 text-6xl mb-2" />
                        <p className="text-gray-600">No projects found</p>
                    </div>
                </td>
              </tr>
            )}
          </tbody>
          {/* Total Amount Row */}
          <tfoot>
            <tr className="bg-gray-200 font-bold">
              <td colSpan="6" className="text-right px-4 py-2">Total Amount</td>
              <td className="px-4 py-2 font-semibold">
                {formatToZMW(filteredProjects.reduce((sum, proj) => sum + parseFloat(proj.amount || 0), 0))}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>

            {/* modal to add */}
        {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded p-5 w-[60%] shadow-lg">
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-4 border-b pb-4">
              <h2 className="text-xl font-semibold">
                {editId?'Edit Project':'Add Project'}
              </h2>
              <button
                onClick={toggleModal}
                className="text-red-700 px-4 py-2 rounded"
              >
                <FaTimes size={30}/>
              </button>
            </div>
            {/* Modal Body */}
            {/* <form onSubmit={editId ? handleEditProject : handleAddProject} > */}
              <FormGenerator fields={fields} initialValues={editId?selectedProject :{}} onSubmit={editId ? handleEditProject : handleAddProject} />

                {/* <div className="grid grid-cols-2 gap-4">
        {fields.map((field) => (
          <div key={field.name}>
            <label className="block text-sm font-medium text-gray-700">{field.label}</label>
            {field.type === "select" ? (
              <Select
              name={field.name}
              required={field.required}
              value={field.options.find((opt) => opt.value === formData[field.name]) || ""}
              onChange={(selectedOption) =>
                setFormData({ ...formData, [field.name]: selectedOption.value })
              }
              options={field.options}
              isSearchable={true}
              placeholder={`Select ${field.label}`}
              className="mt-1"
              styles={{
                control: (base) => ({
                  ...base,
                  padding: "2px",
                  borderRadius: "0.375rem",
                  borderColor: "#D1D5DB",
                  boxShadow: "none",
                  "&:hover": { borderColor: "#6366F1" },
                }),
              }}
            />
            ) : field.type === "textarea" ? (
              <textarea
                name={field.name}
                value={formData[field.name] || ""}
                onChange={handleChange}
                required={field.required}
                className="mt-1 p-2 block w-full border rounded-md shadow-sm focus:ring focus:ring-indigo-300"
              ></textarea>
            ) : (
              <input
                type={field.type}
                name={field.name}
                value={formData[field.name] || ""}
                onChange={handleChange}
                required={field.required}
                className="mt-1 p-2 block w-full border rounded-md shadow-sm focus:ring focus:ring-indigo-300"
              />
            )}
          </div>
        ))}

                </div> */}
        {/* <button
          type="submit"
          className="bg-indigo-600 w-full text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition mt-2"
          disabled={addButton}
        >
          {editId ? "Update Project" : "Add Project"}

        </button>
      </form> */}
            
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-md shadow-lg">
            <h3 className="text-lg font-semibold">Confirm Deletion</h3>
            <p className="text-gray-700">Are you sure you want to delete this project?</p>
            <div className="mt-4 flex justify-end">
              <button onClick={confirmDelete} className="bg-red-600 text-white px-4 py-2 rounded-md mr-2 hover:bg-red-700">
                Yes, Delete
              </button>
              <button onClick={() => setShowDeleteModal(false)} className="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-500">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPage;
